import { useState, useEffect } from 'react';
import { dashboardApi } from '../api';
import { Spinner, Badge, Empty, Icons } from '../components/common';
import toast from 'react-hot-toast';

const fmt = n => Number(n || 0).toLocaleString('en-BD', { minimumFractionDigits: 0 });
const CURRENT_YEAR = new Date().getFullYear();

const STAT_CARDS = [
  { key: 'total_sales',    label: 'Total Sales',    Icon: Icons.ShoppingBag, color: '#B8722A', bg: '#F5E8D8' },
  { key: 'total_purchase', label: 'Total Purchase', Icon: Icons.GRN,         color: '#1A5F8A', bg: '#E6F0F8' },
  { key: 'net_profit',     label: 'Net Profit',     Icon: Icons.TrendUp,     color: '#1A7A4A', bg: '#E8F5EE' },
  { key: 'total_due',      label: 'Total Due',      Icon: Icons.CreditCard,  color: '#C0392B', bg: '#FDECEA' },
];

// Which report "type" + extra params the export endpoint needs, per tab
const EXPORT_TYPE_BY_FILTER = {
  A: 'stock',
  B: 'profit',
  B_MONTHLY: 'profit-monthly',
  C: 'invoice-status',
};

// Tabs where the shared From/To date range applies
const DATE_RANGE_TABS = ['B', 'C', 'D'];

// Triggers a browser download from an axios blob response
function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function DashboardPage() {
  const [summary, setSummary]       = useState(null);
  const [filter, setFilter]         = useState('A');
  const [tableData, setTableData]   = useState([]);
  const [tLoading, setTLoading]     = useState(false);
  const [monthYear, setMonthYear]   = useState(String(CURRENT_YEAR));
  const [monthlyData, setMonthlyData] = useState(null);
  const [availYears, setAvailYears] = useState([]);
  const [exporting, setExporting]   = useState(false);
  // Shared date range — applies to Profit by Brand, Invoice Status, and Customer Report
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');

  useEffect(() => {
    dashboardApi.summary().then(r => setSummary(r.data.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (filter === 'B_MONTHLY') { loadMonthly(monthYear); return; }
    if (filter === 'D') return; // Customer Report manages its own data/loading
    let cancelled = false;
    setTLoading(true);
    const loaders = {
      A: () => dashboardApi.stock(),
      B: () => dashboardApi.profit(fromDate || undefined, toDate || undefined),
      C: () => dashboardApi.invoiceStatus(fromDate || undefined, toDate || undefined),
    };
    loaders[filter]?.()
      .then(r => { if (!cancelled) { setTableData(r.data.data); setTLoading(false); } })
      .catch(() => { if (!cancelled) setTLoading(false); });
    return () => { cancelled = true; };
  }, [filter, fromDate, toDate]);

  const loadMonthly = (year) => {
    setTLoading(true);
    dashboardApi.monthlyProfit(year)
      .then(r => {
        setMonthlyData(r.data.data);
        setAvailYears(r.data.data.years || []);
        setTLoading(false);
      })
      .catch(() => setTLoading(false));
  };

  useEffect(() => {
    if (filter === 'B_MONTHLY') loadMonthly(monthYear);
  }, [monthYear]);

  // Generic export handler — used by every report tab, and passed down
  // to the Customer Report section for its own (mobile-scoped) export.
  const downloadReport = async (type, format, params = {}) => {
    setExporting(true);
    try {
      const res = await dashboardApi.exportReport(type, format, params);
      const mime = format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      let suffix = params.year ? `-${params.year}` : (params.mobile ? `-${params.mobile}` : '');
      if (params.from && params.to) suffix += `-${params.from}_to_${params.to}`;
      saveBlob(new Blob([res.data], { type: mime }), `${type}${suffix}.${format}`);
    } catch (e) {
      toast.error('Export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  const handleExport = (format) => {
    const type = EXPORT_TYPE_BY_FILTER[filter];
    if (!type) return;
    let params = {};
    if (type === 'profit-monthly') params = { year: monthYear };
    else if (DATE_RANGE_TABS.includes(filter) && fromDate && toDate) params = { from: fromDate, to: toDate };
    downloadReport(type, format, params);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Dashboard</h2>
          <p className="page-subtitle">Business performance overview</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        {STAT_CARDS.map(({ key, label, Icon, color, bg }) => (
          <div className="stat-card" key={key} style={{ '--stat-color': color, '--stat-bg': bg }}>
            <div className="stat-card-icon"><Icon size={20} /></div>
            <div className="stat-card-label">{label}</div>
            <div className="stat-card-value">
              ৳{fmt(summary?.[key])}
              <span style={{ fontFamily:'var(--font-body)', fontSize:'.72rem', fontWeight:500, color:'var(--c-text-3)', marginLeft:4, letterSpacing:'.06em', textTransform:'uppercase' }}>BDT</span>
            </div>
          </div>
        ))}
      </div>

      {/* Reports */}
      <div className="card">
        <div className="card-header" style={{ flexWrap:'wrap', gap:10 }}>
          <span style={{ fontFamily:'var(--font-body)', fontSize:'1rem', fontWeight:600, color:'var(--c-text)' }}>Reports</span>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <div className="seg-tabs">
              {[
                ['A',         'Present Stock'],
                ['B',         'Profit by Brand'],
                ['B_MONTHLY', 'Profit by Month'],
                ['C',         'Invoice Status'],
                ['D',         'Customer Report'],
              ].map(([k, label]) => (
                <button key={k} className={`seg-tab ${filter===k?'active':''}`}
                  onClick={() => setFilter(k)}>{label}</button>
              ))}
            </div>
            {DATE_RANGE_TABS.includes(filter) && (
              <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  style={{ width:'auto', height:36, padding:'0 10px', fontSize:'.82rem' }} />
                <span style={{ color:'var(--c-text-3)', fontSize:'.8rem' }}>to</span>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                  style={{ width:'auto', height:36, padding:'0 10px', fontSize:'.82rem' }} />
                {(fromDate || toDate) && (
                  <button className="btn btn-ghost btn-sm" onClick={() => { setFromDate(''); setToDate(''); }}>
                    Clear
                  </button>
                )}
              </div>
            )}
            {filter !== 'D' && (
              <div style={{ display:'flex', gap:6 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => handleExport('xlsx')} disabled={exporting}>
                  Excel
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => handleExport('pdf')} disabled={exporting}>
                  PDF
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ minHeight:320 }}>
          {filter === 'B_MONTHLY' ? (
            <MonthlyProfitSection
              loading={tLoading}
              data={monthlyData}
              year={monthYear}
              availYears={availYears}
              onYearChange={y => setMonthYear(y)}
            />
          ) : filter === 'D' ? (
            <CustomerReportSection onExport={downloadReport} exporting={exporting} fromDate={fromDate} toDate={toDate} />
          ) : (
            tLoading ? <Spinner /> : tableData.length === 0 ? <Empty /> : (
              <div className="table-wrap">
                {filter === 'A' && <StockTable rows={tableData} />}
                {filter === 'B' && <ProfitTable rows={tableData} />}
                {filter === 'C' && <InvoiceStatusTable rows={tableData} />}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Customer Report — ranked list of customers by order count first,
   click any row to drill into their full order + item history ── */
function CustomerReportSection({ onExport, exporting, fromDate, toDate }) {
  const [listQuery, setListQuery]     = useState('');
  const [list, setList]               = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const [selectedMobile, setSelectedMobile] = useState(null);
  const [detail, setDetail]           = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notFound, setNotFound]       = useState(false);

  // Load the customer list — top buyers by order count by default,
  // filtered server-side when the user types a search term, and scoped to
  // the shared From/To date range when set
  useEffect(() => {
    setListLoading(true);
    const t = setTimeout(() => {
      dashboardApi.customersList(listQuery.trim(), undefined, fromDate || undefined, toDate || undefined)
        .then(r => setList(r.data.data))
        .catch(() => setList([]))
        .finally(() => setListLoading(false));
    }, listQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [listQuery, fromDate, toDate]);

  // Re-fetch the open customer's detail if the date range changes while viewing them
  useEffect(() => {
    if (selectedMobile) viewCustomer(selectedMobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const viewCustomer = async (mobile) => {
    setSelectedMobile(mobile);
    setDetail(null); setNotFound(false); setDetailLoading(true);
    try {
      const res = await dashboardApi.customerReport(mobile, fromDate || undefined, toDate || undefined);
      setDetail(res.data.data);
    } catch {
      setNotFound(true);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setSelectedMobile(null); setDetail(null); setNotFound(false); };

  return (
    <div style={{ padding:'16px 22px' }}>
      {/* Search / filter the customer list */}
      <div style={{ marginBottom:16, maxWidth:420 }}>
        <div className="search-box-wrap">
          <span className="search-box-icon"><Icons.Search size={15} /></span>
          <input
            type="text"
            placeholder="Filter by mobile number or name…"
            value={listQuery}
            onChange={e => setListQuery(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:8 }}>
        <div className="section-label" style={{ marginBottom:0 }}>
          Customers ({list.length}) — ranked by number of orders
          {fromDate && toDate && (
            <span style={{ fontWeight:400, color:'var(--c-text-3)', marginLeft:6 }}>({fromDate} to {toDate})</span>
          )}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <button className="btn btn-secondary btn-sm"
            onClick={() => onExport('customer-list', 'xlsx', { q: listQuery || undefined, from: fromDate || undefined, to: toDate || undefined })}
            disabled={exporting}>
            Export List (Excel)
          </button>
          <button className="btn btn-secondary btn-sm"
            onClick={() => onExport('customer-list', 'pdf', { q: listQuery || undefined, from: fromDate || undefined, to: toDate || undefined })}
            disabled={exporting}>
            Export List (PDF)
          </button>
        </div>
      </div>
      <div className="table-wrap" style={{ marginBottom: selectedMobile ? 28 : 0 }}>
        {listLoading ? <Spinner /> : list.length === 0 ? <Empty msg="No customers found" /> : (
          <table className="responsive-table">
            <thead><tr>
              <th>Customer</th><th>Orders</th><th>Items Bought</th>
              <th>Total Revenue</th><th>Total Due</th><th>Last Order</th>
            </tr></thead>
            <tbody>{list.map(c => (
              <tr key={c.customer_mobile}
                onClick={() => viewCustomer(c.customer_mobile)}
                style={{ cursor:'pointer', background: selectedMobile===c.customer_mobile ? 'var(--c-accent-tint)' : undefined }}>
                <td data-label="Customer">
                  <div className="td-primary">{c.customer_name}</div>
                  <div className="td-muted font-mono">{c.customer_mobile}</div>
                </td>
                <td data-label="Orders" className="font-mono">{c.order_count}</td>
                <td data-label="Items Bought" className="font-mono">{c.total_items_qty}</td>
                <td data-label="Total Revenue" className="font-mono">৳{Number(c.total_revenue).toLocaleString()}</td>
                <td data-label="Total Due" className="font-mono text-danger">৳{Number(c.total_due).toLocaleString()}</td>
                <td data-label="Last Order" className="text-muted">{c.last_order_date || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {/* Detail panel for the selected customer */}
      {selectedMobile && (
        <div style={{ marginTop:24, paddingTop:20, borderTop:'2px solid var(--c-border)' }}>
          {detailLoading ? <Spinner /> : notFound ? <Empty msg="No orders found for this customer" /> : detail && (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:18 }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:'1.05rem', color:'var(--c-text)' }}>{detail.customer.customer_name}</div>
                  <div className="td-muted font-mono">{detail.customer.customer_mobile}</div>
                  {fromDate && toDate && (
                    <div style={{ fontSize:'.76rem', color:'var(--c-text-3)', marginTop:2 }}>Showing {fromDate} to {toDate}</div>
                  )}
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => onExport('customer', 'xlsx', { mobile: detail.customer.customer_mobile })}
                    disabled={exporting}>
                    Excel
                  </button>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => onExport('customer', 'pdf', { mobile: detail.customer.customer_mobile })}
                    disabled={exporting}>
                    PDF
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={closeDetail}>Close</button>
                </div>
              </div>

              {/* Summary cards */}
              <div className="summary-grid" style={{ marginBottom:20 }}>
                <div className="stat-card" style={{ '--stat-color':'#B8722A', '--stat-bg':'#F5E8D8' }}>
                  <div className="stat-card-label">Total Orders</div>
                  <div className="stat-card-value">{detail.summary.order_count}</div>
                </div>
                <div className="stat-card" style={{ '--stat-color':'#1A5F8A', '--stat-bg':'#E6F0F8' }}>
                  <div className="stat-card-label">Items Purchased</div>
                  <div className="stat-card-value">{detail.summary.total_items_qty}</div>
                </div>
                <div className="stat-card" style={{ '--stat-color':'#1A7A4A', '--stat-bg':'#E8F5EE' }}>
                  <div className="stat-card-label">Total Revenue</div>
                  <div className="stat-card-value">৳{Number(detail.summary.total_revenue).toLocaleString()}</div>
                </div>
                <div className="stat-card" style={{ '--stat-color':'#C0392B', '--stat-bg':'#FDECEA' }}>
                  <div className="stat-card-label">Total Due</div>
                  <div className="stat-card-value">৳{Number(detail.summary.total_due).toLocaleString()}</div>
                </div>
              </div>

              <div className="section-label">Orders ({detail.orders.length})</div>
              <div className="table-wrap" style={{ marginBottom:24 }}>
                {detail.orders.length === 0 ? <Empty msg="No orders" /> : (
                  <table className="responsive-table">
                    <thead><tr>
                      <th>Invoice #</th><th>Date</th><th>Items</th><th>Qty</th>
                      <th>Total</th><th>Due</th><th>Status</th>
                    </tr></thead>
                    <tbody>{detail.orders.map(o => (
                      <tr key={o.id}>
                        <td data-label="Invoice #" className="td-primary font-mono">{o.inv_number}</td>
                        <td data-label="Date" className="text-muted">{o.inv_date}</td>
                        <td data-label="Items" className="font-mono">{o.line_count}</td>
                        <td data-label="Qty" className="font-mono">{o.total_qty}</td>
                        <td data-label="Total" className="font-mono">৳{Number(o.total_price).toLocaleString()}</td>
                        <td data-label="Due" className="font-mono text-danger">৳{Number(o.due).toLocaleString()}</td>
                        <td data-label="Status"><Badge status={o.status} /></td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>

              <div className="section-label">Items Purchased ({detail.items.length})</div>
              <div className="table-wrap">
                {detail.items.length === 0 ? <Empty msg="No items" /> : (
                  <table className="responsive-table">
                    <thead><tr>
                      <th>Item</th><th>Brand</th><th>Total Qty</th>
                      <th>Avg Unit Price</th><th>Total Amount</th><th>In # Orders</th>
                    </tr></thead>
                    <tbody>{detail.items.map(it => (
                      <tr key={it.item_id}>
                        <td data-label="Item" className="td-primary">{it.item_name}</td>
                        <td data-label="Brand" className="text-muted">{it.brand_name || '—'}</td>
                        <td data-label="Total Qty" className="font-mono">{it.total_qty}</td>
                        <td data-label="Avg Unit Price" className="font-mono">৳{Number(it.avg_unit_price).toLocaleString()}</td>
                        <td data-label="Total Amount" className="font-mono">৳{Number(it.total_amount).toLocaleString()}</td>
                        <td data-label="In # Orders" className="font-mono">{it.order_count}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Monthly Profit Section with accordion drill-down ────── */
function MonthlyProfitSection({ loading, data, year, availYears, onYearChange }) {
  const [expanded, setExpanded]   = useState(null);   // currently open month key
  const [detail, setDetail]       = useState({});      // month -> { invoices, brands, items }
  const [detailLoading, setDL]    = useState({});

  const toggle = async (month) => {
    if (expanded === month) { setExpanded(null); return; }
    setExpanded(month);
    if (detail[month]) return; // already loaded
    setDL(d => ({ ...d, [month]: true }));
    try {
      const res = await dashboardApi.monthlyDetail(month);
      setDetail(d => ({ ...d, [month]: res.data.data }));
    } catch { /* silent */ }
    finally { setDL(d => ({ ...d, [month]: false })); }
  };

  if (loading) return <Spinner />;
  const months = data?.monthly || [];
  const yearOptions = availYears.length > 0 ? availYears : [new Date().getFullYear()];
  const totRev  = months.reduce((s,m) => s + Number(m.total_revenue), 0);
  const totCost = months.reduce((s,m) => s + Number(m.total_cost), 0);
  const totProf = months.reduce((s,m) => s + Number(m.gross_profit), 0);

  return (
    <div style={{ padding:'16px 22px' }}>
      {/* Year selector */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <label style={{ marginBottom:0, whiteSpace:'nowrap' }}>Year</label>
        <select value={year} onChange={e => onYearChange(e.target.value)}
          style={{ width:'auto', minWidth:120 }}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {months.length > 0 && (
          <span style={{ fontSize:'.82rem', color:'var(--c-text-3)', marginLeft:'auto' }}>
            {months.length} month{months.length!==1?'s':''} · click any row to drill down
          </span>
        )}
      </div>

      {months.length === 0 ? (
        <Empty msg={`No delivered invoices found for ${year}`} />
      ) : (
        <div>
          {/* Summary rows — each row is clickable accordion trigger */}
          {months.map(m => {
            const margin   = m.total_revenue > 0 ? ((m.gross_profit/m.total_revenue)*100).toFixed(1) : '0.0';
            const isProfit = parseFloat(m.gross_profit) >= 0;
            const isOpen   = expanded === m.month;
            const det      = detail[m.month];
            const isLoading = detailLoading[m.month];

            return (
              <div key={m.month} style={{ borderBottom:'1px solid var(--c-border)' }}>
                {/* ── Month summary row (clickable) — flexbox so it wraps naturally on mobile ── */}
                <div
                  className="month-row"
                  onClick={() => toggle(m.month)}
                  style={{ background: isOpen ? 'var(--c-accent-tint)' : 'var(--c-surface)' }}
                  onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'var(--c-bg)'; }}
                  onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'var(--c-surface)'; }}
                >
                  <div className="month-row-header">
                    <span className="month-row-title">{m.month_label}</span>
                    <span className="month-row-arrow">{isOpen ? '▲' : '▼'}</span>
                  </div>
                  <div className="month-row-stats">
                    <div className="month-stat">
                      <span className="month-stat-label">Revenue</span>
                      <span className="font-mono" style={{ fontSize:'.88rem', color:'var(--c-text-2)' }}>৳{Number(m.total_revenue).toLocaleString()}</span>
                    </div>
                    <div className="month-stat">
                      <span className="month-stat-label">Cost</span>
                      <span className="font-mono" style={{ fontSize:'.88rem', color:'var(--c-text-2)' }}>৳{Number(m.total_cost).toLocaleString()}</span>
                    </div>
                    <div className="month-stat">
                      <span className="month-stat-label">Profit</span>
                      <span className={`font-mono fw-600 ${isProfit?'text-success':'text-danger'}`} style={{ fontSize:'.9rem' }}>
                        {isProfit?'+':''}৳{Number(m.gross_profit).toLocaleString()}
                      </span>
                    </div>
                    <div className="month-stat">
                      <span className="month-stat-label">Margin</span>
                      <span className={`badge ${isProfit?'badge-active':'badge-due'}`}>{isProfit?'+':''}{margin}%</span>
                    </div>
                  </div>
                </div>

                {/* ── Accordion detail panel ── */}
                {isOpen && (
                  <div style={{ background:'var(--c-bg)', borderTop:'1px solid var(--c-border)', padding:'20px 24px' }}>
                    {isLoading ? (
                      <Spinner />
                    ) : det ? (
                      <MonthDetail detail={det} monthLabel={m.month_label} />
                    ) : (
                      <Empty msg="Failed to load detail" />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Year totals row */}
          <div className="month-total-row">
            <span className="month-row-title" style={{ color:'var(--c-text-3)', fontSize:'.82rem', textTransform:'uppercase', letterSpacing:'.06em' }}>
              Year Total
            </span>
            <div className="month-stat">
              <span className="month-stat-label">Revenue</span>
              <span className="font-mono fw-600">৳{totRev.toLocaleString()}</span>
            </div>
            <div className="month-stat">
              <span className="month-stat-label">Cost</span>
              <span className="font-mono fw-600">৳{totCost.toLocaleString()}</span>
            </div>
            <div className="month-stat">
              <span className="month-stat-label">Profit</span>
              <span className={`font-mono fw-600 ${totProf>=0?'text-success':'text-danger'}`}>
                {totProf>=0?'+':''}৳{totProf.toLocaleString()}
              </span>
            </div>
            {totRev > 0 && (
              <div className="month-stat">
                <span className="month-stat-label">Margin</span>
                <span className={`badge ${totProf>=0?'badge-active':'badge-due'}`}>
                  {totProf>=0?'+':''}{((totProf/totRev)*100).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Accordion detail content for one month ─────────────── */
function MonthDetail({ detail, monthLabel }) {
  const [tab, setTab] = useState('brands'); // 'brands' | 'items' | 'invoices'
  const { invoices=[], brands=[], items=[] } = detail;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
        <span style={{ fontWeight:600, color:'var(--c-text)', fontSize:'.95rem' }}>
          {monthLabel} — Detail
        </span>
        <div className="seg-tabs">
          {[['brands','By Brand'],['items','Top Items'],['invoices','Invoices']].map(([k,label]) => (
            <button key={k} className={`seg-tab ${tab===k?'active':''}`}
              onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
      </div>

      {/* By Brand */}
      {tab === 'brands' && (
        brands.length === 0 ? <Empty msg="No brand data" /> : (
          <div className="table-wrap">
            <table className="responsive-table">
              <thead><tr><th>Brand</th><th>Units Sold</th><th>Revenue</th><th>Cost</th><th>Profit</th><th>Margin</th></tr></thead>
              <tbody>{brands.map((b,i) => {
                const mg = b.revenue > 0 ? ((b.profit/b.revenue)*100).toFixed(1) : '0.0';
                return (
                  <tr key={i}>
                    <td data-label="Brand" className="td-primary">{b.brand_name}</td>
                    <td data-label="Units Sold" className="font-mono">{b.units_sold}</td>
                    <td data-label="Revenue" className="font-mono">৳{Number(b.revenue).toLocaleString()}</td>
                    <td data-label="Cost" className="font-mono">৳{Number(b.cost).toLocaleString()}</td>
                    <td data-label="Profit" className={`font-mono fw-600 ${b.profit>=0?'text-success':'text-danger'}`}>
                      {b.profit>=0?'+':''}৳{Number(b.profit).toLocaleString()}
                    </td>
                    <td data-label="Margin">
                      <span className={`badge ${b.profit>=0?'badge-active':'badge-due'}`}>
                        {b.profit>=0?'+':''}{mg}%
                      </span>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )
      )}

      {/* Top Items */}
      {tab === 'items' && (
        items.length === 0 ? <Empty msg="No item data" /> : (
          <div className="table-wrap">
            <table className="responsive-table">
              <thead><tr><th>Item</th><th>Brand</th><th>Units</th><th>Revenue</th><th>Cost</th><th>Profit</th></tr></thead>
              <tbody>{items.map((it,i) => (
                <tr key={i}>
                  <td data-label="Item" className="td-primary">{it.item_name}</td>
                  <td data-label="Brand" className="text-muted">{it.brand_name}</td>
                  <td data-label="Units" className="font-mono">{it.units_sold}</td>
                  <td data-label="Revenue" className="font-mono">৳{Number(it.revenue).toLocaleString()}</td>
                  <td data-label="Cost" className="font-mono">৳{Number(it.cost).toLocaleString()}</td>
                  <td data-label="Profit" className={`font-mono fw-600 ${it.profit>=0?'text-success':'text-danger'}`}>
                    {it.profit>=0?'+':''}৳{Number(it.profit).toLocaleString()}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      )}

      {/* Invoices */}
      {tab === 'invoices' && (
        invoices.length === 0 ? <Empty msg="No invoices" /> : (
          <div className="table-wrap">
            <table className="responsive-table">
              <thead><tr><th>Invoice #</th><th>Date</th><th>Customer</th><th>Revenue</th><th>Cost</th><th>Profit</th></tr></thead>
              <tbody>{invoices.map(inv => (
                <tr key={inv.id}>
                  <td data-label="Invoice #" className="td-primary font-mono">{inv.inv_number}</td>
                  <td data-label="Date" className="text-muted">{inv.inv_date}</td>
                  <td data-label="Customer">
                    <div>{inv.customer_name}</div>
                    <div className="td-muted font-mono">{inv.customer_mobile}</div>
                  </td>
                  <td data-label="Revenue" className="font-mono">৳{Number(inv.total_price).toLocaleString()}</td>
                  <td data-label="Cost" className="font-mono">৳{Number(inv.total_cost).toLocaleString()}</td>
                  <td data-label="Profit" className={`font-mono fw-600 ${inv.profit>=0?'text-success':'text-danger'}`}>
                    {inv.profit>=0?'+':''}৳{Number(inv.profit).toLocaleString()}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

/* ── Other report tables ─────────────────────────────────── */
function StockTable({ rows }) {
  return (
    <table className="responsive-table">
      <thead><tr><th>Item</th><th>Brand / Color</th><th>Stock</th><th>Sell Price</th><th title="Perpetual weighted average cost">Avg Cost</th></tr></thead>
      <tbody>{rows.map(r => (
        <tr key={r.id}>
          <td data-label="Item" className="td-primary">{r.item_name}</td>
          <td data-label="Brand / Color"><div>{r.brand_name}</div><div className="td-muted">{r.color_variation}</div></td>
          <td data-label="Stock"><span className={`badge ${Number(r.stock_quantity)>0?'badge-active':'badge-inactive'}`}>{r.stock_quantity??0}</span></td>
          <td data-label="Sell Price" className="font-mono">৳{Number(r.price).toLocaleString()}</td>
          <td data-label="Avg Cost" className="font-mono">৳{Number(r.cost_price).toLocaleString()}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function ProfitTable({ rows }) {
  return (
    <table className="responsive-table">
      <thead><tr>
        <th>Brand</th><th>Total Revenue</th><th>Total Cost</th>
        <th>Net Profit</th><th>Margin %</th>
      </tr></thead>
      <tbody>{rows.map((r,i) => {
        const margin = r.total_revenue > 0
          ? ((r.gross_profit / r.total_revenue) * 100).toFixed(1)
          : '0.0';
        const isProfit = r.gross_profit >= 0;
        return (
          <tr key={i}>
            <td data-label="Brand" className="td-primary">{r.brand_name}</td>
            <td data-label="Total Revenue" className="font-mono">৳{Number(r.total_revenue).toLocaleString()}</td>
            <td data-label="Total Cost" className="font-mono">৳{Number(r.total_cost).toLocaleString()}</td>
            <td data-label="Net Profit" className={`font-mono fw-600 ${isProfit?'text-success':'text-danger'}`}>
              {isProfit?'+':''}৳{Number(r.gross_profit).toLocaleString()}
            </td>
            <td data-label="Margin %">
              <span className={`badge ${isProfit?'badge-active':'badge-due'}`}>
                {isProfit?'+':''}{margin}%
              </span>
            </td>
          </tr>
        );
      })}</tbody>
    </table>
  );
}

function InvoiceStatusTable({ rows }) {
  return (
    <table className="responsive-table">
      <thead><tr><th>Invoice #</th><th>Customer</th><th>Total</th><th>Due</th><th>Status</th><th>Courier</th></tr></thead>
      <tbody>{rows.map(r => (
        <tr key={r.id}>
          <td data-label="Invoice #"><div className="td-primary font-mono">{r.inv_number}</div><div className="td-muted">{r.inv_date}</div></td>
          <td data-label="Customer"><div className="td-primary">{r.customer_name}</div><div className="td-muted">{r.customer_mobile}</div></td>
          <td data-label="Total" className="font-mono">৳{Number(r.total_price).toLocaleString()}</td>
          <td data-label="Due" className="font-mono text-danger fw-500">৳{Number(r.due).toLocaleString()}</td>
          <td data-label="Status"><Badge status={r.invoice_status} /></td>
          <td data-label="Courier"><div>{r.courier_name||'—'}</div>{r.courier_tracking_number&&<div className="td-muted font-mono">{r.courier_tracking_number}</div>}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}